import DefaultPic from '@assets/images/ai1.png';
import PlanImg from '@assets/images/profile-plan.png';
import { ButtonCore, InputCore, InputPasswordCore } from '@components';
import {
  useUpdateProfileInfoFn,
  useGetSocialMediaData,
  useUpdatePassword,
  useUpdateProfileImage,
} from 'src/core/repo/profile/useCase';
import {
  Col,
  Form,
  Row,
  Modal,
  DatePicker,
  RadioChangeEvent,
  Radio,
  Skeleton,
  Select,
} from 'antd';
import { ChangeEvent, useEffect, useState } from 'react';

import { toast } from 'react-toastify';
import styles from './index.module.scss';
import {
  useProfileInfo,
  useUpdateChildrenVisible,
  useUpdateProfileVisible,
} from 'src/core/repo/profile/useCase';
import { IUserInfo } from 'src/core/repo/profile/type';
import { useQueryClient } from 'react-query';
import dayjs from 'dayjs';
import { CheckboxGroupProps } from 'antd/es/checkbox';
import AddSocialMediaModal from './_components/AddSocialMediaModal';
import { Input } from 'antd';

interface FormFields {
  name: string;
  value: string | number | boolean;
}

const Profile = () => {
  const { mutate, isLoading, isSuccess } = useUpdatePassword();
  const [fields, setFields] = useState<Array<FormFields>>([]);
  const [profileData, setProfileData] = useState<IUserInfo>();
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] =
    useState(false);
  const [form] = Form.useForm();
  const [imageName, setImageName] = useState<string | null>();
  const [isPrivate, setIsPrivate] = useState<'public' | 'private'>('public');
  const [isVisibleChild, setIsVisibleChild] = useState<'visible' | 'hidden'>(
    'visible',
  );

  const { TextArea } = Input;
  const plainOptions: CheckboxGroupProps<string>['options'] = ['Boy', 'Girl'];
  const [imageUrl, setImageUrl] = useState<string | ArrayBuffer | null>();
  const [gender, setGender] = useState('Boy');
  const [isSocialMediaModalOpen, setIsSocialMediaModalOpen] = useState(false);

  const updateProfileImageMutation = useUpdateProfileImage();

  const { data: socialMediaData, isLoading: isSocialMediaDataLoading } =
    useGetSocialMediaData();
  const profileInfo = useProfileInfo();
  const updateChildrenVisible = useUpdateChildrenVisible();
  const updateProfileVisible = useUpdateProfileVisible();
  const queryClient = useQueryClient();

  const handleChildVisibility = (status: number) => {
    const id = profileInfo.data?.profile_info?.ProfileID;
    if (!id) return;
    setIsVisibleChild(status === 1 ? 'visible' : 'hidden');
    updateChildrenVisible.mutate(
      {
        profile_id: id,
        children_visibility: status,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['profile-info'] });
        },
      },
    );
  };

  const handleProfileVisibility = (status: number) => {
    const id = profileInfo.data?.profile_info?.ProfileID;
    if (!id) return;
    setIsPrivate(status === 1 ? 'public' : 'private');
    updateProfileVisible.mutate(
      {
        profile_id: id,
        profile_visibility: status,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['profile-info'] });
        },
      },
    );
  };

  const handleChangePassword = (values: { new_password: string }) => {
    mutate(values.new_password, {
      onSuccess: () => {
        toast.success('Your password changed successfully');
        setIsChangePasswordModalOpen(false);
        // Clear form fields
        form.resetFields();
      },
      onError: (error: any) => {
        toast.error(
          (error?.response?.data?.message as string) ||
            'Failed to change password',
        );
      },
    });
  };
  const updateProfileInfo = useUpdateProfileInfoFn();

  const handleSubmit = (values: any) => {
    console.log('submit');

    updateProfileInfo.mutate(
      {
        ProfileID: profileInfo.data?.profile_info.ProfileID ?? 0,
        UserID: profileInfo.data?.profile_info.UserID ?? 0,

        FullName: values.FullName,
        DisplayName: values.displayName,
        BirthDate: values.birthDate
          ? values.birthDate.format('YYYY/MM/DD')
          : null,
        Bio: values.bio,
        Handle: values.email,
        ProfileVisibility:
          profileInfo?.data?.profile_info?.ProfileVisibility ?? false,
        ChildrenVisibility:
          profileInfo?.data?.profile_info?.ChildrenVisibility ?? false,
        GenderID: parseFloat(values.gender as string),
      },
      {
        onSuccess(data, variables, context) {
          queryClient.invalidateQueries(['profile-info']);
        },
      },
    );
  };

  const handleImageChange = (info: ChangeEvent<HTMLInputElement>) => {
    const file = info.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Selected file is not an image');
      return;
    }

    // Optional: 5MB limit
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    // Keep filename
    setImageName(file.name);

    const reader = new FileReader();
    reader.onload = function (this: FileReader, ev: ProgressEvent<FileReader>) {
      const buffer = ev.target?.result as ArrayBuffer | null;
      const profileId = profileInfo.data?.profile_info?.ProfileID;
      if (!buffer || !profileId) return;

      const bytes = new Uint8Array(buffer);
      let binary = '';
      for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      const base64 = window.btoa(binary);
      const payload = `data:${file.type};base64,${base64}`;
      // For frontend display
      setImageUrl(payload);

      updateProfileImageMutation.mutate(
        {
          profile_id: profileId,
          photo: payload,
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['profile-info'] });
          },
        },
      );
    };
    reader.readAsArrayBuffer(file);
  };

  useEffect(() => {
    if (profileInfo.data?.profile_info) {
      const info = profileInfo.data.profile_info;
      setIsPrivate(info.ProfileVisibility ? 'public' : 'private');
      setIsVisibleChild(info.ChildrenVisibility ? 'visible' : 'hidden');
    }
  }, [profileInfo.data]);

  useEffect(() => {
    if (profileInfo?.data?.profile_info) {
      form.setFieldsValue({
        FullName: profileInfo.data.profile_info.FullName,
        displayName: profileInfo.data.profile_info.DisplayName,
        birthDate: profileInfo.data.profile_info.BirthDate
          ? dayjs(profileInfo.data.profile_info.BirthDate, 'YYYY/MM/DD')
          : null,
        bio: profileInfo.data.profile_info.Bio,
        email: profileInfo.data.profile_info.UserName,
        'public-url': `app.tadatoon/${profileInfo.data.profile_info.Handle}`,
        'referral-code': `app.tadatoon/${profileInfo.data.profile_info.ReferalCode}`,
        gender: profileInfo.data.profile_info.Gender,
      });
    }
  }, [profileInfo, form]);

  return (
    <div className={styles['container']}>
      <Row gutter={[16, 16]} align={'stretch'} justify={'center'}>
        <Col xl={10} xs={24}>
          <div className={styles['profile_plan']}>
            <div className={styles['plan_card']}>
              <h2>My plan : Free</h2>
              <h2>{profileData?.LeftStoryNumbers} stories left</h2>
              <ButtonCore className={styles['upgrade_btn']}>
                Upgrade !
              </ButtonCore>
            </div>
            <img src={PlanImg} alt="my plan" className={styles['plan_img']} />
          </div>
        </Col>
        <Col xl={14} xs={24}>
          <div className={styles['profile_form']}>
            <div>
              <Form
                className="profile-form"
                fields={fields}
                labelCol={{ span: 24 }}
                form={form}
                onFinish={handleSubmit}>
                <Row gutter={40} justify={'space-between'} align={'middle'}>
                  <Col md={24} xs={24}>
                    <Form.Item label="Profile Picture">
                      <div className={styles['upload_box']}>
                        <div className={styles['d-flex']}>
                          {(() => {
                            return (
                              <img
                                src={
                                  profileInfo.data?.profile_info.ProfileImage
                                    ? `https://${profileInfo.data?.profile_info.ProfileImage}`
                                    : DefaultPic
                                }
                                alt="Info Image"
                                className={styles['info_img']}
                              />
                            );
                          })()}
                        </div>
                        <input
                          type="file"
                          className={styles['input']}
                          onChange={handleImageChange}
                        />
                      </div>
                    </Form.Item>
                  </Col>
                  <Col md={24} xs={24}>
                    <Form.Item label="Full Name" name="FullName">
                      <InputCore />
                    </Form.Item>
                  </Col>
                  <Col md={24} xs={24}>
                    <Form.Item label="Display Name" name="displayName">
                      <InputCore />
                    </Form.Item>
                  </Col>
                  <Col md={24} xs={24}>
                    <Form.Item label="Birth Date" name="birthDate">
                      <DatePicker
                        className={styles.datePicker}
                        defaultValue={dayjs(
                          profileInfo.data?.profile_info.BirthDate
                            ? profileInfo.data?.profile_info.BirthDate
                            : '',
                          'YYYY/MM/DD',
                        )}
                        format={'YYYY/MM/DD'}
                      />
                    </Form.Item>
                  </Col>
                  <Col md={24} xs={24}>
                    <Form.Item label="Gender" name="gender">
                      <Select
                        style={{
                          height: '45px',
                          border: 'none',
                          outline: 'none',
                          borderRadius: '20px',
                        }}>
                        <Select.Option value="1">Male</Select.Option>
                        <Select.Option value="2">Female</Select.Option>
                        <Select.Option value="3">
                          Perefer not to say
                        </Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col md={24} xs={24}>
                    <Form.Item label="Bio" name="bio">
                      <TextArea
                        rows={5}
                        defaultValue={profileInfo.data?.profile_info.Bio || ''}
                      />
                    </Form.Item>
                  </Col>

                  <Col md={24} xs={24}>
                    <Form.Item label="Email" name="email">
                      <InputCore
                        readOnly
                        defaultValue={
                          profileInfo.data?.profile_info.Handle || ''
                        }
                      />
                    </Form.Item>
                  </Col>
                  <Col md={24} xs={24}>
                    <div>
                      {!socialMediaData || isSocialMediaDataLoading ? (
                        Array.from({ length: 3 }).map((_, index) => (
                          <Skeleton key={index} active />
                        ))
                      ) : (
                        <div className={styles.socialMediaList}>
                          {profileInfo.data?.social_links?.map((item) => (
                            <div
                              className={styles.socialMediaItem}
                              key={item.SocialMediaID}>
                              <p>{item.SocialMedia}</p>
                              <img
                                src={`https://${item.IconURL}`}
                                alt={item.IconURL}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <Form.Item label="Social media link" name="Social-media">
                      <ButtonCore
                        block
                        className={styles.socialBtn}
                        onClick={() => setIsSocialMediaModalOpen(true)}>
                        Add another
                      </ButtonCore>
                    </Form.Item>
                  </Col>
                  <Col md={24} xs={24}>
                    <Form.Item label="Public URL" name="public-url">
                      <InputCore
                        defaultValue={
                          `app.tadatoon/${profileInfo.data?.profile_info.Handle}` ||
                          ''
                        }
                      />
                    </Form.Item>
                  </Col>

                  <Col md={24} xs={24}>
                    <Form.Item label="Referral code" name="referral-code">
                      <InputCore
                        readOnly
                        defaultValue={
                          `app.tadatoon/${profileInfo.data?.profile_info.ReferalCode}` ||
                          ''
                        }
                      />
                    </Form.Item>
                  </Col>

                  <Col md={24} xs={24}>
                    <div>
                      <p className={styles.label}>Account Privacy</p>
                      <div className={styles.buttonWrapper}>
                        <ButtonCore
                          onClick={() => handleProfileVisibility(0)}
                          className={
                            profileInfo.data?.profile_info
                              ?.ProfileVisibility === false
                              ? styles.activeItem
                              : styles.disableItem
                          }>
                          Private
                        </ButtonCore>
                        <ButtonCore
                          onClick={() => handleProfileVisibility(1)}
                          className={
                            profileInfo.data?.profile_info?.ProfileVisibility
                              ? styles.activeItem
                              : styles.disableItem
                          }>
                          Public
                        </ButtonCore>
                      </div>
                    </div>
                  </Col>

                  <Col md={24} xs={24}>
                    <div>
                      <p className={styles.label}>Show my children</p>
                      <div className={styles.buttonWrapper}>
                        <ButtonCore
                          onClick={() => handleChildVisibility(1)}
                          className={
                            profileInfo.data?.profile_info?.ChildrenVisibility
                              ? styles.activeItem
                              : styles.disableItem
                          }>
                          Show
                        </ButtonCore>
                        <ButtonCore
                          onClick={() => handleChildVisibility(0)}
                          className={
                            profileInfo.data?.profile_info
                              ?.ChildrenVisibility === false
                              ? styles.activeItem
                              : styles.disableItem
                          }>
                          Hide
                        </ButtonCore>
                      </div>
                    </div>
                  </Col>

                  <Col xxl={24} xl={24} md={24} xs={24}>
                    <Form.Item>
                      <ButtonCore
                        className={styles.changePasswordBtn}
                        block
                        type="default"
                        onClick={() => setIsChangePasswordModalOpen(true)}>
                        Change Password
                      </ButtonCore>
                    </Form.Item>
                  </Col>
                  <Col xxl={24} xl={24} md={24} xs={24}>
                    <Form.Item>
                      <ButtonCore block type="primary" htmlType="submit">
                        Submit
                      </ButtonCore>
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </div>

            <Modal
              title={null}
              open={isSocialMediaModalOpen}
              onCancel={() => setIsSocialMediaModalOpen(false)}
              footer={null}
              width={358}
              centered
              closeIcon={null}
              className={styles.socialMediaModal}>
              <AddSocialMediaModal
                socialMediaData={socialMediaData || []}
                isLoading={isSocialMediaDataLoading}
                setIsSocialMediaModalOpen={setIsSocialMediaModalOpen}
                ProfileID={profileInfo.data?.profile_info?.ProfileID ?? null}
              />
            </Modal>

            <Modal
              title="Change Your Password"
              open={isChangePasswordModalOpen}
              onCancel={() => {
                setIsChangePasswordModalOpen(false);
                form.resetFields();
              }}
              footer={null}
              width={600}>
              <Form
                form={form}
                className="profile-form"
                labelCol={{ span: 24 }}
                onFinish={handleChangePassword}>
                <Row gutter={40} align={'bottom'} justify={'space-between'}>
                  <Col md={24} xs={24}>
                    <Form.Item
                      label="New Password"
                      name="new_password"
                      rules={[
                        { required: true, message: '' },
                        {
                          min: 8,
                          message:
                            'Your password must be at least 8 characters',
                        },
                        ({ getFieldValue }) => ({
                          validator(_rule, value) {
                            if (value === getFieldValue('current_password')) {
                              return Promise.reject(
                                'Passwords must be different',
                              );
                            } else {
                              return Promise.resolve();
                            }
                          },
                        }),
                      ]}>
                      <InputPasswordCore />
                    </Form.Item>
                  </Col>
                  <Col md={24} xs={24}>
                    <Form.Item
                      label="Confirm New Password"
                      name="confirmPassword"
                      rules={[
                        { required: true, message: '' },
                        ({ getFieldValue }) => ({
                          validator(_rule, value) {
                            if (value !== getFieldValue('new_password')) {
                              return Promise.reject('Passwords did not match');
                            } else {
                              return Promise.resolve();
                            }
                          },
                        }),
                      ]}>
                      <InputPasswordCore />
                    </Form.Item>
                  </Col>
                  <Col xxl={24} xl={24} md={24} xs={24}>
                    <Form.Item>
                      <ButtonCore
                        loading={isLoading}
                        block
                        type="primary"
                        htmlType="submit">
                        Change Password
                      </ButtonCore>
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </Modal>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default Profile;
